let loading: Promise<void> | undefined;

/**
 * Defines the `<lottie-player>` custom element on first use.
 *
 * The player used to be a render-blocking `<script>` from unpkg in `index.html`
 * (Lighthouse: 440 ms on every page, third-party origin). It is now a lazy
 * chunk built on lottie-web's LIGHT build: SVG renderer only, and no expression
 * engine, so nothing calls `eval` and the site's CSP needs no 'unsafe-eval'.
 * The animations under assets/lottie were rewritten with their one expression
 * (`loopOut('pingpong')`) baked into plain keyframes, so the light build shows
 * exactly what the full player did.
 *
 * `<lottie-player>` tags already in the DOM upgrade as soon as the element is
 * defined, so templates keep the `src`, `loop`, `autoplay`, `speed` and
 * `background` attributes they always had.
 */
export function ensureLottiePlayer(): Promise<void> {
    if (!loading) {
        loading = customElements.get('lottie-player')
            ? Promise.resolve()
            : import('lottie-web/build/player/lottie_light').then(m => defineLottiePlayer((m as any).default ?? m));
    }
    return loading;
}

interface LottieLike {
    loadAnimation(params: {
        container: Element;
        renderer: 'svg';
        loop: boolean;
        autoplay: boolean;
        path: string;
        rendererSettings?: { preserveAspectRatio?: string };
    }): { destroy(): void; setSpeed(speed: number): void };
}

function defineLottiePlayer(lottie: LottieLike): void {
    if (customElements.get('lottie-player')) {
        return;
    }

    class LottiePlayerElement extends HTMLElement {
        private anim?: { destroy(): void; setSpeed(speed: number): void };

        static get observedAttributes(): string[] {
            return ['src'];
        }

        connectedCallback(): void {
            if (!this.style.display) {
                this.style.display = 'block';
            }
            this.load();
        }

        disconnectedCallback(): void {
            this.anim?.destroy();
            this.anim = undefined;
        }

        attributeChangedCallback(): void {
            if (this.isConnected) {
                this.load();
            }
        }

        private load(): void {
            const src = this.getAttribute('src');
            if (!src) {
                return;
            }
            this.anim?.destroy();
            this.replaceChildren();
            const background = this.getAttribute('background');
            if (background) {
                this.style.background = background;
            }
            this.anim = lottie.loadAnimation({
                container: this,
                renderer: 'svg',
                loop: this.hasAttribute('loop'),
                autoplay: this.hasAttribute('autoplay'),
                path: src,
                rendererSettings: { preserveAspectRatio: 'xMidYMid meet' }
            });
            const speed = parseFloat(this.getAttribute('speed') || '1');
            if (speed && speed !== 1) {
                this.anim.setSpeed(speed);
            }
        }
    }

    customElements.define('lottie-player', LottiePlayerElement);
}
